import React from 'react'
import styled from 'styled-components/macro'
import { OLLIE_MOMENT_COPY } from 'src/utils/ollieMoments'

/**
 * "Meet the new Ollie" (once) or "Your week" (weekly brief), in place of the
 * opening message. The finding cards below still carry the buttons.
 */
const OllieMomentCard = ({
  moment,
  onAsk,
  onTodayJob,
  onDismiss,
  disabled = false,
}) => {
  if (!moment) return null
  return (
    <Card aria-label={moment.heading}>
      <Label>Ollie</Label>
      <Heading>{moment.heading}</Heading>
      {moment.intro && <Line>{moment.intro}</Line>}
      {moment.lines.map(line => (
        <Line key={line}>{line}</Line>
      ))}
      <Buttons>
        {moment.ask && moment.askLabel && (
          <PrimaryButton
            type='button'
            disabled={disabled}
            onClick={() => onAsk(moment)}
          >
            {moment.askLabel}
          </PrimaryButton>
        )}
        {moment.todayJob?.path && (
          <PrimaryButton
            type='button'
            disabled={disabled}
            onClick={() => onTodayJob(moment.todayJob.path)}
          >
            {moment.todayJob.label}
          </PrimaryButton>
        )}
        <QuietButton type='button' onClick={onDismiss}>
          {OLLIE_MOMENT_COPY.dismiss}
        </QuietButton>
      </Buttons>
      {moment.todayJob?.note && (
        <Note data-testid='ollie-moment-today-job-note'>
          {moment.todayJob.note}
        </Note>
      )}
      {moment.feedback && (
        <Feedback>
          {moment.feedback.text}
          {moment.feedback.mailto && (
            <>
              {' '}
              <a href={moment.feedback.mailto}>
                {moment.feedback.email || OLLIE_MOMENT_COPY.emailStephen}
              </a>
            </>
          )}
        </Feedback>
      )}
    </Card>
  )
}

const Card = styled.div`
  background: ${({ theme }) => theme.colours.mayoWhite};
  border: 1px solid ${({ theme }) => theme.colours.panSilver};
  border-top: 3px solid ${({ theme }) => theme.colours.lettuceGreen};
  border-radius: 14px 14px 14px 4px;
  padding: 14px 16px;
  margin-bottom: 14px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
`

const Label = styled.span`
  display: block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colours.oliveGrey};
  margin-bottom: 4px;
`

const Heading = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.4;
  color: ${({ theme }) => theme.colours.bbqBlack};
`

const Line = styled.p`
  margin: 6px 0 0;
  font-size: 14px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colours.bbqBlack};
`

const Note = styled.p`
  margin: 10px 0 0;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colours.oliveGrey};
`

const Buttons = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
`

const PrimaryButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colours.lettuceGreen};
  background: ${({ theme }) => theme.colours.lettuceGreen};
  color: ${({ theme }) => theme.colours.bbqBlack};
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`

const QuietButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colours.panSilver};
  background: ${({ theme }) => theme.colours.mayoWhite};
  color: ${({ theme }) => theme.colours.bbqBlack};
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 14px;
  cursor: pointer;
`

const Feedback = styled.p`
  margin: 12px 0 0;
  padding-top: 10px;
  border-top: 1px solid ${({ theme }) => theme.colours.panSilver};
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colours.oliveGrey};

  a {
    color: ${({ theme }) => theme.colours.bbqBlack};
    font-weight: 600;
    text-decoration: underline;
  }
`

export default OllieMomentCard
